import React from 'react';

export default function MatrixDecompositionExplanation() {
  return (
    <div className="w-full p-1 text-xs text-gray-800 text-justify leading-relaxed hyphens-auto md:text-sm">
      <p>
        <strong>Goal:</strong> Factor a matrix <code>A ∈ ℝⁿˣⁿ</code> into structured components (like triangular or orthogonal matrices) to simplify solving systems, compute eigenvalues, or analyze properties.
      </p>

      <p className="mt-3"><strong>Mathematical Explanation:</strong></p>
      <ul className="list-disc pl-5 space-y-4 mb-4">
        <li>
          <strong>LU Decomposition:</strong> Decomposes <code>A</code> into a lower-triangular matrix <code>L</code> and an upper-triangular matrix <code>U</code>.
          <pre className="bg-white p-2 border border-gray-200 rounded text-xs text-center">
            A = L·U &nbsp;or &nbsp; P·A = L·U
          </pre>
          Useful for solving <code>A·x = b</code> for various <code>b ∈ ℝⁿ</code>.
        </li>
        <li>
          <strong>Cholesky Decomposition:</strong> Applicable only to symmetric, positive-definite matrices.
          <pre className="bg-white p-2 border border-gray-200 rounded text-xs text-center">
            A = L·Lᵗ
          </pre>
          This method is numerically more efficient and stable than LU for such matrices.
        </li>
        <li>
          <strong>QR Decomposition:</strong> Decomposes <code>A</code> into an orthogonal matrix <code>Q</code> and an upper-triangular matrix <code>R</code>.
          <pre className="bg-white p-2 border border-gray-200 rounded text-xs text-center">
            A = Q·R
          </pre>
          Useful in least squares problems and iterative eigenvalue estimation.
        </li>
      </ul>
      <ul className="list-disc pl-5 space-y-2 mb-4">
        <li>
          <strong>Pivoting (LU):</strong> Enables row reordering for numerical stability: <code>P·A = L·U</code>.
        </li>
        <li>
          <strong>Iterative QR:</strong> Repeatedly applies QR steps to converge toward an upper triangular matrix whose diagonal approximates the eigenvalues of <code>A</code>.
        </li>
      </ul>
      <p className="mt-3"><strong>Convergence (iterative QR):</strong></p>
        <ul className="list-disc pl-5 space-y-2 mb-4">
          <li>
            In the iterative QR algorithm, starting from <code>A<sub>k</sub></code>, the decomposition  
            <code> A<sub>k</sub> = Q<sub>k</sub> R<sub>k</sub></code> is computed and in the next step,
            <code> A<sub>k+1</sub> = R<sub>k</sub> Q<sub>k</sub></code> is set. 
            The diagonal of <code>A<sub>k</sub></code> converges to the eigenvalues of <code>A</code>.
          </li>
          <li>
            <strong>Tolerance:</strong> Stopping criterion based on successive eigenvalue estimates. 
            Let <code>λ<sup>(k)</sup></code> denote the diagonal vector of <code>A<sub>k</sub></code>. 
            Iteration stops once
            <pre className="bg-white p-2 border border-gray-200 rounded text-xs text-center">
              ‖λ<sup>(k)</sup> − λ<sup>(k−1)</sup>‖<sub>∞</sub> &lt; tolerance
            </pre>
            i.e. the maximum absolute difference between diagonal entries falls below the tolerance.
          </li>
          <li>
            <strong>Maximum iterations (max_iter):</strong> Hard upper bound on the number of QR steps. 
            If reached, iteration stops even if the tolerance is not satisfied.
          </li>
        </ul>

      <p className="mt-3"><strong>Input format:</strong></p>
      <ul className="list-disc pl-5 space-y-1 mb-2">
        <li>Provide a square matrix in JSON format:</li>
        <pre className="bg-white p-2 border border-gray-200 rounded text-xs mt-1">
          [[12, -51, 4], [6, 167, -68], <br /> [-4, 24, -41]]
        </pre>
        <li>Matrix must be square for LU and QR.</li>
        <li>For Cholesky: matrix must be symmetric and positive definite.</li>
      </ul>

      <p className="mt-3"><strong>Output:</strong></p>
      <ul className="list-disc pl-5 space-y-1 mb-2">
        <li>Matrices L, U, Q, R, and optionally permutation matrix P (for LU).</li>
        <li>For iterative QR: step logs, convergence info, and estimated eigenvalues.</li>
      </ul>
    </div>
  );
}
